import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronRight, Minus, Plus, Leaf, FileText, ChevronLeft, ChevronRight as ArrowRight, Truck, Zap, Star, ShieldCheck, ShieldAlert, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [farmers, setFarmers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewPerm, setReviewPerm] = useState({ can_review: false });
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get(`/products/${productId}`).then(r => {
        setProduct(r.data);
        if (r.data.variants?.length > 0) setSelectedVariant(r.data.variants[0]);
        setActiveImage(0);
      }).catch(() => {}),
      api.get(`/farmer-products/product/${productId}`).then(r => setFarmers(r.data || [])).catch(() => setFarmers([])),
      api.get(`/reviews/product/${productId}`).then(r => setReviews(r.data || [])).catch(() => setReviews([])),
    ]).finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    if (user && productId) {
      api.get(`/reviews/can-review/${productId}`).then(r => setReviewPerm(r.data || { can_review: false })).catch(() => {});
    }
  }, [user, productId]);

  const handleAddToCart = async () => {
    if (!user) { navigate('/auth'); return; }
    if (!selectedVariant) { toast.error('Please select a variant'); return; }
    try {
      await addToCart(product.product_id, selectedVariant.name, quantity);
      toast.success('Added to cart!');
    } catch {
      toast.error('Failed to add to cart');
    }
  };

  const submitReview = async () => {
    setSubmittingReview(true);
    try {
      const res = await api.post('/reviews', {
        product_id: productId,
        order_id: reviewPerm.order_id,
        rating: reviewRating,
        review_text: reviewText,
      });
      toast.success('Thank you for your review!');
      setReviews([res.data, ...reviews]);
      setReviewPerm({ can_review: false, already_reviewed: true });
      setReviewText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit review');
    } finally { setSubmittingReview(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center"><p>Product not found</p></div>;

  const images = (product.images || []).filter(Boolean);
  if (images.length === 0) images.push('');
  const deliveryDays = product.delivery_days ?? product.expected_delivery_days;
  const productReports = Array.isArray(product.lab_reports) ? product.lab_reports.filter(r => r && (r.url || r.name)) : [];
  const hasProductReport = (product.lab_report_url && product.lab_report_url !== '#') || productReports.length > 0;
  const discount = Number(product.discount_percentage || 0);
  const original = Number(product.original_price || 0);
  const currentPrice = (selectedVariant?.price || 0) * quantity;
  const avgRating = Number(product.avg_rating || 0);
  const reviewCount = Number(product.review_count || 0);

  const deliveryLabel = deliveryDays === 0 ? 'Same Day Delivery' : deliveryDays === 1 ? 'Next Day Delivery' : `Delivery in ${deliveryDays || 2} Days`;

  // Rating histogram
  const hist = [0,0,0,0,0];
  reviews.forEach(r => { if (r.rating >= 1 && r.rating <= 5) hist[r.rating - 1]++; });

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to={`/category/${product.category}`} className="hover:text-foreground transition-colors capitalize">{product.category?.replace(/-/g, ' ')}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground font-medium">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Image Gallery */}
        <div>
          <div className="relative rounded-3xl overflow-hidden aspect-square bg-muted group">
            <img data-testid="product-image" src={images[activeImage]} alt={product.name} className="w-full h-full object-cover" />
            {/* Discount + Special tag badges */}
            {discount > 0 && (
              <div className="absolute top-4 left-4 bg-neutral-900 text-white text-xs font-semibold px-3 py-1.5 rounded-full">{discount}% Off</div>
            )}
            {product.special_tag && (
              <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5 rounded-full">{product.special_tag}</div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={() => setActiveImage(a => (a - 1 + images.length) % images.length)}
                  data-testid="prev-image-btn"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => setActiveImage(a => (a + 1) % images.length)}
                  data-testid="next-image-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImage(i)}
                  data-testid={`thumbnail-${i}`}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${i === activeImage ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge variant="secondary" className="rounded-full text-xs">{product.category}</Badge>
            {product.subcategory && <Badge variant="outline" className="rounded-full text-xs">{product.subcategory}</Badge>}
            {product.variety && <Badge variant="outline" className="rounded-full text-xs">{product.variety}</Badge>}
            {deliveryDays === 0 && <Badge data-testid="same-day-badge" className="rounded-full text-xs bg-primary/10 text-primary border-primary/20 gap-1"><Zap className="h-3 w-3" /> Same Day Delivery</Badge>}
            {deliveryDays === 1 && <Badge data-testid="next-day-badge" className="rounded-full text-xs bg-primary/10 text-primary border-primary/20 gap-1"><Zap className="h-3 w-3" /> Next Day Delivery</Badge>}
            {deliveryDays > 1 && <Badge data-testid="delivery-days-badge" variant="outline" className="rounded-full text-xs gap-1"><Truck className="h-3 w-3" /> {deliveryLabel}</Badge>}
          </div>
          <h1 data-testid="product-name" className="text-3xl sm:text-4xl font-serif font-medium mb-3">{product.name}</h1>

          {/* Rating summary */}
          {avgRating > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < Math.round(avgRating) ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
                ))}
              </div>
              <span className="text-sm font-semibold">{avgRating.toFixed(1)}</span>
              <a href="#reviews" className="text-xs text-muted-foreground hover:text-foreground">({reviewCount} reviews)</a>
            </div>
          )}

          <Separator className="my-6" />

          {/* Variants */}
          {product.variants?.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Select Size</p>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((v) => (
                  <button key={v.name} data-testid={`variant-${v.name.replace(/\s/g, '-').toLowerCase()}`}
                    onClick={() => setSelectedVariant(v)}
                    className={`px-5 py-3 rounded-2xl border text-sm font-medium transition-all ${selectedVariant?.name === v.name ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                    <span className="block font-semibold">{v.weight}</span>
                    <span className="block text-xs mt-0.5">{'\u20B9'}{v.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mb-6">
            <p className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Quantity</p>
            <div className="inline-flex items-center gap-4 bg-muted rounded-full p-1">
              <button data-testid="quantity-decrease" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-background transition-colors">
                <Minus className="h-4 w-4" />
              </button>
              <span data-testid="quantity-value" className="text-sm font-semibold w-6 text-center">{quantity}</span>
              <button data-testid="quantity-increase" onClick={() => setQuantity(quantity + 1)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-background transition-colors">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Price + Add to Cart */}
          <div className="flex items-center gap-6 mb-8 flex-wrap">
            <div>
              {original > 0 && original > (selectedVariant?.price || 0) && (
                <span className="text-sm text-muted-foreground line-through mr-2">{'\u20B9'}{original * quantity}</span>
              )}
              <span className="text-3xl font-semibold">{'\u20B9'}{currentPrice}</span>
              <p className="text-xs text-muted-foreground mt-1">{quantity} x {selectedVariant?.weight || ''}</p>
            </div>
            <Button data-testid="add-to-cart-button" onClick={handleAddToCart} size="lg" className="rounded-full px-10" disabled={product.stock <= 0}>
              {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
            </Button>
          </div>

          <Separator className="my-8" />

          {/* About */}
          <div className="mb-8">
            <h3 className="font-serif font-medium text-lg mb-3">About this Product</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{product.description}</p>
          </div>

          {product.growing_notes && (
            <div className="mb-8">
              <h3 className="font-serif font-medium text-lg mb-3 flex items-center gap-2"><Leaf className="h-4 w-4 text-primary" /> Growing Notes</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{product.growing_notes}</p>
            </div>
          )}

          {/* Product-level lab reports ONLY (farm-level reports moved to farmer profile) */}
          {hasProductReport && (
            <div className="mb-8">
              <h3 className="font-serif font-medium text-lg mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Product Lab Reports</h3>
              <div className="space-y-2">
                {product.lab_report_url && product.lab_report_url !== '#' && (
                  <div data-testid="legacy-product-report" className="flex items-center gap-3 p-3 bg-card rounded-2xl border">
                    <FileText className="h-5 w-5 text-primary shrink-0" />
                    <span className="text-sm flex-1">Lab Report</span>
                    <a href={product.lab_report_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline font-medium">View</a>
                  </div>
                )}
                {productReports.map((r, i) => (
                  <div key={i} data-testid={`product-report-${i}`} className="flex items-center gap-3 p-3 bg-card rounded-2xl border">
                    <FileText className="h-5 w-5 text-primary shrink-0" />
                    <span className="text-sm flex-1">{r.name || `Report ${i+1}`}</span>
                    {r.url && r.url !== '#' && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline font-medium">View</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Refund Policy */}
          <div className="mb-8 bg-secondary/30 rounded-3xl p-5">
            <h3 className="font-serif font-medium text-base mb-2 flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-primary" /> Refund Policy</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {product.refund_policy || 'At Orgaanix, we take quality seriously. If you receive a product that does not meet our quality standards, please contact us within 24 hours of delivery with a photo. We will arrange a replacement or refund on a case-by-case basis.'}
            </p>
          </div>
        </div>
      </div>

      {/* Sourced From Our Farmers — full width section */}
      {farmers.length > 0 && (
        <section data-testid="sourced-from-farmers" className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif font-medium">Sourced From Our Farmers</h2>
            <span className="text-xs text-muted-foreground">{farmers.length} {farmers.length === 1 ? 'farmer' : 'farmers'}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {farmers.map((link) => {
              const f = link.farmer || link;
              return (
                <div key={f.farmer_id} data-testid={`farmer-card-${f.farmer_id}`} className="bg-card rounded-3xl border border-border/50 p-5 flex gap-4">
                  <img src={f.profile_image} alt={f.name} className="w-16 h-16 rounded-2xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <h4 className="font-serif font-medium text-base truncate">{f.name}</h4>
                      <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                      <MapPin className="h-3 w-3" /> {f.farm_name} &middot; {f.location}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{f.story}</p>
                    <Link to={`/farmer/${f.farmer_id}`}>
                      <Button data-testid={`view-farmer-${f.farmer_id}`} variant="outline" size="sm" className="rounded-full h-8 text-xs">View Profile</Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Reviews section (product-specific) */}
      <section id="reviews" data-testid="reviews-section" className="mt-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-2xl font-serif font-medium">Ratings & Reviews</h2>
          <span className="text-xs text-muted-foreground">{reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Rating summary */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-3xl border border-border/50 p-6">
              <div className="text-center mb-4">
                <p className="text-5xl font-serif font-medium">{avgRating.toFixed(1)}</p>
                <div className="flex items-center justify-center gap-0.5 mt-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < Math.round(avgRating) ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{reviewCount} reviews</p>
              </div>
              <div className="space-y-1.5">
                {[5,4,3,2,1].map(n => {
                  const count = hist[n-1];
                  const pct = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
                  return (
                    <div key={n} className="flex items-center gap-2 text-xs">
                      <span className="w-3 text-muted-foreground">{n}</span>
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-amber-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-6 text-right text-muted-foreground">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Write review */}
            <div className="mt-4 bg-card rounded-3xl border border-border/50 p-6">
              {!user ? (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-3">Sign in to write a review</p>
                  <Link to="/auth"><Button variant="outline" size="sm" className="rounded-full">Sign In</Button></Link>
                </div>
              ) : reviewPerm.already_reviewed ? (
                <p className="text-sm text-center text-muted-foreground">You have already reviewed this product.</p>
              ) : !reviewPerm.can_review ? (
                <p className="text-sm text-center text-muted-foreground">Purchase this product to leave a review.</p>
              ) : (
                <div>
                  <h4 className="font-medium mb-3 text-sm">Write a Review</h4>
                  <div className="flex items-center gap-1 mb-3">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => setReviewRating(n)} data-testid={`star-${n}`}>
                        <Star className={`h-6 w-6 transition-colors ${n <= reviewRating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30 hover:text-amber-400'}`} />
                      </button>
                    ))}
                  </div>
                  <Textarea data-testid="review-text-input" value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Share your experience..." rows={3} className="rounded-xl" />
                  <Button data-testid="submit-review-btn" onClick={submitReview} disabled={submittingReview} className="mt-3 rounded-full w-full">
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Review list */}
          <div className="lg:col-span-2 space-y-3">
            {reviews.length === 0 ? (
              <div className="bg-card rounded-3xl border border-border/50 p-10 text-center">
                <p className="text-sm text-muted-foreground">No reviews yet. Be the first to review!</p>
              </div>
            ) : (
              reviews.map((r) => (
                <div key={r.id} data-testid={`review-item-${r.id}`} className="bg-card rounded-3xl border border-border/50 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">{r.customer_name?.[0] || 'C'}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{r.customer_name}</p>
                        {r.verified_purchase && <p className="text-[10px] text-primary flex items-center gap-1"><ShieldCheck className="h-2.5 w-2.5" /> Verified Purchase</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed">{r.review_text}</p>
                  <p className="text-[11px] text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Product-level FAQ only (no general/basket mixing) */}
      {Array.isArray(product.faq) && product.faq.length > 0 && (
        <section data-testid="product-faq-section" className="mt-16 max-w-3xl">
          <h2 className="text-2xl font-serif font-medium mb-6">Product Questions</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {product.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`product-faq-${i}`} className="bg-card rounded-2xl border border-border/50 px-5">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{f.question || f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground whitespace-pre-wrap pb-5">{f.answer || f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}
    </div>
  );
}

function FaqBlock({ items }) {
  if (!items || items.length === 0) return <p className="text-sm text-muted-foreground text-center py-10">No questions here yet.</p>;
  return (
    <Accordion type="single" collapsible className="space-y-2 max-w-3xl">
      {items.map((f, i) => (
        <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`} className="bg-card rounded-2xl border border-border/50 px-5">
          <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{f.question || f.q}</AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground whitespace-pre-wrap pb-5">{f.answer || f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
